import React, { useEffect, useState } from 'react';
import { MapPin, Gift, Info } from 'lucide-react';
import { getData } from '../helpers/ApiSettings';

const DeliverySelection = ({
  selectedMethod,
  selectedZone,
  selectedRelayPoint,
  deliveryPrice,
  onMethodChange,
  onZoneChange,
  onRelayPointChange,
  isSubscription = false,
  freeDelivery = false,
  // Colissimo (intra-DOM, sale checkout only — off for rental/loisir)
  colissimoEnabled = false,
  colissimoAvailable = false,
  colissimoRate = null,
  colissimoCartWeight = null,
  colissimoZone = null,
  colissimoWithSignature = false,
  onColissimoSignatureChange = () => {}
}) => {
    const [relayPoints, setRelayPoints] = useState([]);
    const [deliveryZones, setDeliveryZones] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedZoneDetails, setSelectedZoneDetails] = useState(null);

    useEffect(() => {
        fetchData();
    }, [])

    const fetchData = async () => {
        try{
            const respRelay = await getData('/relay-points');
            const respZones = await getData('/delivery-zones');
            setRelayPoints(respRelay?.data?.data);
            setDeliveryZones(respZones?.data?.data)
        }catch(e){
            console.error("Error fetching delivery data: ", e);
        }
    };

    const formatPrice = (price) => {
        if (freeDelivery) {
            return (
                <span>
                    <span className="text-decoration-line-through text-muted me-2">
                        {price.toFixed(2)}€
                    </span>
                    <span className="text-success fw-bold">GRATUIT</span>
                </span>
            );
        }
        return `${price.toFixed(2)}€`;
    };

    const renderModal = () => {
        if (!selectedZoneDetails) return null;
        
        // Split cities string and format each city name
        const citiesList = selectedZoneDetails.cities.split(',').map(city => {
            return city
                .trim()
                .toLowerCase()
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join('-');
        });
        
        return (
            <>
                <div 
                    className={`modal-backdrop fade ${showModal ? 'show' : ''}`}
                    style={{ display: showModal ? 'block' : 'none' }}
                ></div>
    
                <div className={`modal fade ${showModal ? 'show' : ''}`} 
                     style={{ display: showModal ? 'block' : 'none', zIndex: 1050 }}
                     tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {selectedZoneDetails.name} ({freeDelivery ? 'GRATUIT' : `${selectedZoneDetails.price.toFixed(2)}€`})
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <h6>Villes desservies :</h6>
                                <ul className="list-unstyled mb-0">
                                    {citiesList.map((city, index) => (
                                        <li key={index} className="mb-1">
                                            • {city}
                                        </li>
                                    ))}
                                </ul>
                                {freeDelivery && (
                                    <div className="alert alert-success mt-3 mb-0">
                                        <Gift size={16} className="me-2" />
                                        <strong>Livraison gratuite</strong> avec votre abonnement mensuel !
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowModal(false)}
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="d-flex align-items-center mb-3">
          <h3 className="card-title mb-0">Mode de livraison</h3>
          {freeDelivery && (
            <span className="badge bg-success ms-2">
              <Gift size={14} className="me-1" />
              GRATUIT
            </span>
          )}
        </div>

        {freeDelivery && (
          <div className="alert alert-success mb-3">
            <Gift size={18} className="me-2" />
            <strong>Avantage abonnement :</strong> La livraison est offerte avec votre abonnement mensuel !
          </div>
        )}
        
        <div className="mb-4">
          <div className="form-check mb-3">
            <input
              type="radio"
              id="relay_point"
              name="delivery_method"
              className="form-check-input"
              value="relay_point"
              checked={selectedMethod === 'relay_point'}
              onChange={(e) => {
                onMethodChange(e.target.value);
                onZoneChange(''); // Reset zone when switching to relay point
                onRelayPointChange(null);
              }}
            />
            <label className="form-check-label" htmlFor="relay_point">
              Point relais <small><a href="/points-relais" target="_blank">(Voir la liste)</a></small>
              {freeDelivery && <span className="badge bg-success ms-2">GRATUIT</span>}
            </label>
          </div>
          
          <div className="form-check">
            <input
              type="radio"
              id="home_delivery"
              name="delivery_method"
              className="form-check-input"
              value="home_delivery"
              checked={selectedMethod === 'home_delivery'}
              onChange={(e) => {
                onMethodChange(e.target.value);
                onRelayPointChange(null); // Reset relay point when switching to home delivery
              }}
            />
            <label className="form-check-label" htmlFor="home_delivery">
              Livraison à domicile
              {freeDelivery && <span className="badge bg-success ms-2">GRATUIT</span>}
            </label>
          </div>

          {colissimoEnabled && colissimoAvailable && (
            <div className="form-check mt-3">
              <input
                type="radio"
                id="colissimo"
                name="delivery_method"
                className="form-check-input"
                value="colissimo"
                checked={selectedMethod === 'colissimo'}
                onChange={(e) => {
                  onMethodChange(e.target.value);
                  onZoneChange('');
                  onRelayPointChange(null);
                }}
              />
              <label className="form-check-label" htmlFor="colissimo">
                Colissimo La Poste <small className="text-muted">(livraison à domicile, calculée selon le poids)</small>
              </label>
            </div>
          )}

          {colissimoEnabled && !colissimoAvailable && (
            <div className="alert alert-light border mt-3 mb-0 py-2 px-3 small text-muted">
              <Info size={14} className="me-1" />
              {colissimoCartWeight && colissimoCartWeight.totalKg > 30
                ? `Colissimo indisponible : panier > 30 kg (limite La Poste)`
                : colissimoZone === 'unsupported'
                ? `Colissimo disponible uniquement pour une livraison vers les DOM (Antilles, Guyane, Réunion, Mayotte). Renseignez votre code postal.`
                : colissimoCartWeight && colissimoCartWeight.weightedCount === 0
                ? `Colissimo indisponible : aucun article du panier n'a de poids renseigné.`
                : `Colissimo indisponible pour ce panier.`}
            </div>
          )}
        </div>

        {selectedMethod === 'colissimo' && colissimoRate && (
          <div className="border rounded p-3 mb-3 bg-light">
            <div className="d-flex justify-content-between mb-2">
              <span>Poids total du colis :</span>
              <strong>{colissimoCartWeight?.totalKg?.toFixed(2)} kg</strong>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span>Tranche tarifaire :</span>
              <span>jusqu'à {colissimoRate.bracket.maxKg} kg</span>
            </div>
            {colissimoCartWeight && !colissimoCartWeight.hasAllWeights && (
              <div className="small text-muted mt-2 mb-2">
                <Info size={12} className="me-1" />
                Certains articles n'ont pas de poids renseigné — non comptés dans le calcul ({colissimoCartWeight.missingProductTitles.slice(0, 2).join(', ')}{colissimoCartWeight.missingProductTitles.length > 2 ? '…' : ''}).
              </div>
            )}
            <div className="form-check mt-2">
              <input
                type="checkbox"
                id="colissimo_signature"
                className="form-check-input"
                checked={colissimoWithSignature}
                onChange={(e) => onColissimoSignatureChange(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="colissimo_signature">
                Recommandé avec signature à la livraison
                <span className="text-muted ms-1">
                  (+{(colissimoRate.bracket.priceWithSig - colissimoRate.bracket.priceNoSig).toFixed(2)} €)
                </span>
              </label>
            </div>
          </div>
        )}

        {selectedMethod === 'home_delivery' && (
        <div className="mt-3">
            <label className="form-label">Zone de livraison</label>
            <div className="d-flex gap-2">
                <select 
                    className="form-select"
                    value={selectedZone}
                    onChange={(e) => {
                        const selectedZoneObject = deliveryZones.find(zone => zone.id === parseInt(e.target.value));
                        onZoneChange(selectedZoneObject);
                    }}
                    required
                >
                    <option value="">Sélectionnez votre zone</option>
                    {deliveryZones.map((zone) => (
                        <option 
                            key={zone.id}
                            value={zone.id}
                        >
                            {zone.name} ({freeDelivery ? 'GRATUIT' : `${zone.price.toFixed(2)}€`})
                        </option>
                    ))}
                </select>
                
                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                        const zoneDetails = deliveryZones.find(zone => zone.id === parseInt(selectedZone));
                        setSelectedZoneDetails(zoneDetails);
                        setShowModal(true);
                    }}
                    disabled={!selectedZone}
                >
                    Voir les villes
                </button>
            </div>
        </div>
    )}

        {selectedMethod === 'relay_point' && (
          <div className="mt-3">
            <label className="form-label">Point relais</label>
            <div className="list-group">
              {relayPoints.map((point) => (
                <label 
                  key={point.id} 
                  className={`list-group-item list-group-item-action ${
                    selectedRelayPoint?.id === point.id ? 'active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="relay_point"
                    className="form-check-input me-2"
                    checked={selectedRelayPoint?.id === point.id}
                    onChange={() => onRelayPointChange(point)}
                    required={selectedMethod === 'relay_point'}
                  />
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{point.name}</strong>
                      <div className="text-muted small">
                        <MapPin size={14} className="me-1" />
                        {point.address}, {point.postal_code} {point.city}
                      </div>
                    </div>
                    <div className="text-end">
                      {freeDelivery ? (
                        <span className="badge bg-success">GRATUIT</span>
                      ) : (
                        <span className="fw-bold">{point.price ? `${point.price.toFixed(2)}€` : '0€'}</span>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {((selectedMethod === 'home_delivery' && selectedZone) ||
          (selectedMethod === 'relay_point' && selectedRelayPoint) ||
          (selectedMethod === 'colissimo' && colissimoRate)) && (
          <div className="mt-3">
            <strong>
              Prix de livraison: {freeDelivery ? (
                <span>
                  <span className="text-decoration-line-through text-muted me-2">
                    {deliveryPrice.toFixed(2)}€
                  </span>
                  <span className="text-success">GRATUIT</span>
                </span>
              ) : (
                `${deliveryPrice.toFixed(2)}€`
              )}
            </strong>
          </div>
        )}

        {freeDelivery && (selectedMethod === 'home_delivery' || selectedMethod === 'relay_point') && (
          <div className="alert alert-info mt-3 mb-0">
            <strong>Note :</strong> La livraison restera gratuite tant que vous maintenez un abonnement mensuel actif.
          </div>
        )}
      </div>
      {renderModal()}
    </div>
  );
};

export default DeliverySelection;